import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import Box from "@ui-kit/layout/Box/Box.web";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import React from "react";
import { AnalyticsService } from "@shared/services/analytics";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { UsePublicBasketType } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";

export type AddonProductCardProps = {
  openAddonProductModal: () => void;
  product: ProductTypeSerializer;
  publicBasket: UsePublicBasketType;
  allInPricing?: boolean;
  fitToParent?: boolean;
  setSelectedProduct: (product: ProductTypeSerializer | null) => void;
  analyticsSource?: string;
};

const fitToParentStyles = {
  container: {
    width: "100%",
    maxWidth: "100%",
  },
};

export const AddonProductCard: React.FC<AddonProductCardProps> = ({
  openAddonProductModal,
  product,
  publicBasket,
  allInPricing,
  fitToParent = false,
  setSelectedProduct,
  analyticsSource,
}) => {
  const defaultImage = formatRelativeStaticPath(
    "images/default-showpass-square.png"
  );

  const { t } = useTranslation("checkout");

  const {
    image,
    name,
    product_attributes: productAttributes,
    currency,
  } = product;

  if (!currency) return null;

  const handleActivation = () => {
    AnalyticsService.buttonClick({
      label: "addons",
      custom_params: {
        datum: "card_click",
        type: "product",
        id: product.id,
        ...(analyticsSource && { source: analyticsSource }),
      },
    });

    setSelectedProduct(product);
    openAddonProductModal();
  };

  const priceRange = getPriceRange({
    items: productAttributes,
    allInPricing: Boolean(allInPricing),
    venueCurrency: currency,
    paymentType: publicBasket.paymentType,
    purchasePlatform: publicBasket.purchaseSourcePlatform,
  });

  return (
    <Button
      as="div"
      role="button"
      variant="unstyled"
      tabIndex={0}
      p="none"
      w="element.11xl"
      height="auto"
      style={fitToParent ? fitToParentStyles.container : undefined}
      onClick={handleActivation}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivation();
        }
      }}
      data-testid={`addon-product-card-${product.id}`}
      aria-label={t("View details for {{name}}, price {{priceRange}}", {
        priceRange,
        name,
      })}
    >
      <Flex direction="column" w="100%">
        <Box
          w="100%"
          aspectRatio="1"
          mb="md"
          overflow="hidden"
          borderRadius="md"
          position="relative"
          flexShrink={0}
        >
          <CardImage
            src={image || defaultImage}
            alt={`Product image for ${name}`}
            fill
            objectFit="cover"
          />
        </Box>
        <Flex direction="column" height="auto">
          <Text
            fontWeight="bold"
            color="font.dark"
            fontSize="sm"
            noOfLines={2}
            minH="element.3xl"
          >
            {name ?? ""}
          </Text>
          <Box mt="auto">
            <Text
              data-testid={`addon-product-card-${product.id}-price`}
              color={{ _light: "gray.600", _dark: "gray.700" }}
              fontSize="sm"
            >
              {priceRange}
            </Text>
          </Box>
        </Flex>
      </Flex>
    </Button>
  );
};
